import {
  AppNavigation, Bookmark, MenuMode, NavigationAction, NavigationContext, NavigationNode, NavigationNodeType,
} from '@app/modules/shared/models/navigation';
import {ICON_PATTERN, normalizeInAppUrl} from '@app/_helpers/navigation-preferences';

/**
 * The menus the server sent, made safe to render and resolved for what is on screen. Pure
 * functions, no Angular: the store hands out frozen objects, so nothing here mutates its input.
 */

const TYPES: NavigationNodeType[] = ['group', 'link', 'action', 'divider', 'widget'];
const ACTIONS: NavigationAction[] = ['logout', 'stopImpersonation', 'themeSwitcher', 'menuMode'];
const MAX_DEPTH = 4;
const ROUTE = /^\/(?!\/)[^\s\\?#]*$/;

export interface ResolvedNavNode {
  id: string;
  type: NavigationNodeType;
  label: string;
  icon: string | null;
  /** The in-app path with `:projectId` filled in; null for groups, actions and external links. */
  path: string | null;
  queryParams: {[key: string]: string} | null;
  href: string | null;
  external: boolean;
  exact: boolean;
  context: NavigationContext | null;
  action: NavigationAction | null;
  keywords: string[];
  menu: 'main' | 'user';
  depth: number;
  parentId: string | null;
  /** The labels of the groups above it, outermost first. */
  ancestors: string[];
  children: ResolvedNavNode[];
}

function validRoute(route: any): boolean {
  if (typeof route !== 'string' || !ROUTE.test(route)) {
    return false;
  }
  return (route.match(/:[A-Za-z]\w*/g) || []).every((placeholder) => placeholder === ':projectId');
}

function validHref(href: any): boolean {
  return typeof href === 'string' && /^https?:\/\/[^\s]+$/i.test(href);
}

function normalizeNodes(list: any, depth: number, seen: Set<string>): NavigationNode[] {
  if (!Array.isArray(list) || depth > MAX_DEPTH) {
    return [];
  }
  return list.reduce((nodes: NavigationNode[], raw: any) => {
    if (!raw || typeof raw !== 'object' || !TYPES.includes(raw.type)) {
      return nodes;
    }
    if (typeof raw.id !== 'string' || !raw.id || seen.has(raw.id)) {
      return nodes;
    }
    const label = typeof raw.label === 'string' ? raw.label.trim() : '';
    if (!label && raw.type !== 'divider') {
      return nodes;
    }
    const node: NavigationNode = {id: raw.id, type: raw.type};
    if (label) {
      node.label = label;
    }
    if (raw.type !== 'divider' && typeof raw.icon === 'string' && ICON_PATTERN.test(raw.icon)) {
      node.icon = raw.icon;
    }
    if (raw.context === 'global' || raw.context === 'project') {
      node.context = raw.context;
    }
    if (Array.isArray(raw.keywords)) {
      node.keywords = raw.keywords.filter((keyword: any) => typeof keyword === 'string').slice(0, 12);
    }
    if (raw.type === 'link') {
      if (validRoute(raw.route)) {
        node.route = raw.route;
        if (raw.queryParams && typeof raw.queryParams === 'object') {
          node.queryParams = Object.keys(raw.queryParams).reduce((params, key) => {
            if (typeof raw.queryParams[key] === 'string') {
              params[key] = raw.queryParams[key];
            }
            return params;
          }, {} as {[key: string]: string});
        }
        if (raw.exact === true) {
          node.exact = true;
        }
      } else if (validHref(raw.href)) {
        node.href = raw.href;
        node.target = '_blank';
      } else {
        return nodes;
      }
    }
    if (raw.type === 'action' || raw.type === 'widget') {
      if (!ACTIONS.includes(raw.action)) {
        return nodes;
      }
      node.action = raw.action;
    }
    if (raw.type === 'group') {
      node.children = normalizeNodes(raw.children, depth + 1, seen);
    }
    seen.add(raw.id);
    nodes.push(node);
    return nodes;
  }, []);
}

/** The payload as the client will trust it, or null when there is none worth rendering. */
export function normalizeNavigation(raw: any): AppNavigation | null {
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.main) || !Array.isArray(raw.user)) {
    return null;
  }
  const seen = new Set<string>();
  return {main: normalizeNodes(raw.main, 1, seen), user: normalizeNodes(raw.user, 1, seen)};
}

function tidyDividers(nodes: ResolvedNavNode[]): ResolvedNavNode[] {
  const tidy = nodes.reduce((list: ResolvedNavNode[], node) => {
    const previous = list[list.length - 1];
    if (node.type === 'divider' && (!previous || previous.type === 'divider')) {
      return list;
    }
    return [...list, node];
  }, []);
  if (tidy.length && tidy[tidy.length - 1].type === 'divider') {
    tidy.pop();
  }
  return tidy;
}

export interface ResolveContext {
  projectId: number | null;
  /**
   * Top mode swaps the menu in and out of a project, as the panel always has: "Projects"
   * outside one, the project's own pages inside. The side menu has room for both, so its
   * global entries stay put and the project's pages appear beneath them.
   */
  mode: MenuMode;
  menu?: 'main' | 'user';
}

/** Filter by project context, fill `:projectId`, prune what is left empty. */
export function resolveTree(nodes: NavigationNode[] | null | undefined, context: ResolveContext,
  depth = 1, parentId: string | null = null, ancestors: string[] = [],
  inherited: NavigationContext | null = null): ResolvedNavNode[] {
  const menu = context.menu || 'main';
  const inProject = context.projectId !== null && context.projectId !== undefined;
  return tidyDividers((nodes || []).reduce((resolved: ResolvedNavNode[], node) => {
    const nodeContext = node.context || inherited;
    if (nodeContext === 'project' && !inProject) {
      return resolved;
    }
    if (nodeContext === 'global' && inProject && context.mode === 'top') {
      return resolved;
    }
    let path: string | null = null;
    if (node.route) {
      if (node.route.includes(':projectId') && !inProject) {
        return resolved;
      }
      path = node.route.replace(/:projectId\b/g, String(context.projectId));
    }
    const result: ResolvedNavNode = {
      id: node.id,
      type: node.type,
      label: node.label || '',
      icon: node.icon || null,
      path,
      queryParams: node.queryParams && Object.keys(node.queryParams).length ? {...node.queryParams} : null,
      href: node.href || null,
      external: !!node.href,
      exact: !!node.exact,
      context: nodeContext,
      action: node.action || null,
      keywords: [...(node.keywords || [])],
      menu,
      depth,
      parentId,
      ancestors,
      children: [],
    };
    if (node.type === 'group') {
      result.children = resolveTree(node.children, context, depth + 1, node.id,
        [...ancestors, result.label], nodeContext);
      if (!result.children.some((child) => child.type !== 'divider')) {
        return resolved;
      }
    }
    resolved.push(result);
    return resolved;
  }, []));
}

/** Every node, depth first. */
export function flattenNodes(nodes: ResolvedNavNode[]): ResolvedNavNode[] {
  return nodes.reduce((all: ResolvedNavNode[], node) => [...all, node, ...flattenNodes(node.children)], []);
}

/** Every link, depth first - what search, bookmarks and the active trail look through. */
export function flattenLinks(nodes: ResolvedNavNode[]): ResolvedNavNode[] {
  return flattenNodes(nodes).filter((node) => node.type === 'link');
}

export function findNode(nodes: ResolvedNavNode[], id: string): ResolvedNavNode | null {
  return flattenNodes(nodes).find((node) => node.id === id) || null;
}

export interface ParsedUrl {
  path: string;
  segments: string[];
  query: {[key: string]: string};
}

/** Path, segments and query of an in-app URL; the origin, fragment and matrix params dropped. */
export function parseInAppUrl(url: string): ParsedUrl {
  let parsed: URL;
  try {
    parsed = new URL(url || '/', 'https://in-app.invalid');
  } catch (_) {
    parsed = new URL('/', 'https://in-app.invalid');
  }
  const segments = parsed.pathname.split('/')
    .map((segment) => segment.split(';')[0])
    .filter(Boolean)
    .map((segment) => {
      try {
        return decodeURIComponent(segment);
      } catch (_) {
        return segment;
      }
    });
  const query: {[key: string]: string} = {};
  parsed.searchParams.forEach((value, key) => {
    query[key] = value;
  });
  return {path: '/' + segments.join('/'), segments, query};
}

export interface NavTrail {
  node: ResolvedNavNode;
  /** The groups above it, outermost first. */
  groups: ResolvedNavNode[];
  /**
   * The URL is this entry's page itself, not a page beneath it - so bookmarking the page is
   * bookmarking the entry.
   */
  exact: boolean;
}

/**
 * Which entry the URL belongs to: an exact path with matching query first, then an exact
 * path, then the longest entry whose path the URL sits under. Query parameters only ever
 * break ties - a list that writes `?page=2` into its own URL stays highlighted. `navParent`
 * (route data) names the entry outright, for pages that live elsewhere in the URL space.
 */
export function findActiveTrail(nodes: ResolvedNavNode[], url: string, navParent?: string | null): NavTrail | null {
  const all = flattenNodes(nodes);
  const byId = new Map(all.map((node) => [node.id, node]));
  const groupsOf = (node: ResolvedNavNode): ResolvedNavNode[] => {
    const groups: ResolvedNavNode[] = [];
    let parent = node.parentId ? byId.get(node.parentId) : null;
    while (parent) {
      groups.unshift(parent);
      parent = parent.parentId ? byId.get(parent.parentId) : null;
    }
    return groups;
  };
  if (navParent && byId.has(navParent)) {
    const node = byId.get(navParent);
    return {node, groups: groupsOf(node), exact: false};
  }
  const current = parseInAppUrl(url);
  let best: {node: ResolvedNavNode; score: number; exact: boolean} | null = null;
  for (const node of all) {
    if (node.type !== 'link' || !node.path) {
      continue;
    }
    const target = parseInAppUrl(node.path);
    const same = target.segments.length === current.segments.length
      && target.segments.every((segment, index) => segment === current.segments[index]);
    const under = !node.exact && target.segments.length > 0
      && target.segments.length < current.segments.length
      && target.segments.every((segment, index) => segment === current.segments[index]);
    if (!same && !under) {
      continue;
    }
    const params = node.queryParams || {};
    const keys = Object.keys(params);
    const matching = keys.filter((key) => current.query[key] === params[key]).length;
    const tier = same ? (matching === keys.length ? 3 : 2) : 1;
    const score = tier * 10000 + target.segments.length * 100 + matching;
    if (!best || score > best.score) {
      const exact = same && matching === keys.length
        && Object.keys(current.query).every((key) => key in params);
      best = {node, score, exact};
    }
  }
  return best ? {node: best.node, groups: groupsOf(best.node), exact: best.exact} : null;
}

export interface NavCrumb {
  label: string;
  path: string | null;
  queryParams: {[key: string]: string} | null;
  current: boolean;
}

export interface BreadcrumbInput {
  trail: NavTrail | null;
  project: {id: number; title: string | null} | null;
  /** Where the project crumb leads: its campaigns, or its share page for an influencer. */
  projectHome: string | null;
  /** The "Projects" entry, when the user has one. */
  projectsNode: ResolvedNavNode | null;
  /** A name the page gave itself (a campaign's, an influencer's), or its route's navTitle. */
  leaf: string | null;
}

/** Projects › project › the entry's groups › the entry › the page's own name. */
export function buildBreadcrumb(input: BreadcrumbInput): NavCrumb[] {
  const crumbs: NavCrumb[] = [];
  const add = (label: string, path: string | null = null, queryParams: {[key: string]: string} | null = null) => {
    if (label) {
      crumbs.push({label, path, queryParams, current: false});
    }
  };
  const trail = input.trail;
  if (input.project) {
    if (input.projectsNode?.path) {
      add(input.projectsNode.label, input.projectsNode.path);
    }
    add(input.project.title || `Project #${input.project.id}`, input.projectHome);
  }
  if (trail) {
    if (trail.node.menu === 'user') {
      add('Account');
    }
    trail.groups.forEach((group) => add(group.label));
    const alreadyThere = input.project && trail.node.id === input.projectsNode?.id;
    if (!alreadyThere) {
      add(trail.node.label, trail.node.path, trail.node.queryParams);
    }
  }
  const leaf = (input.leaf || '').trim();
  const last = crumbs[crumbs.length - 1];
  if (leaf && (!last || last.label.toLowerCase() !== leaf.toLowerCase())) {
    add(leaf);
  }
  if (crumbs.length) {
    const final = crumbs[crumbs.length - 1];
    crumbs[crumbs.length - 1] = {...final, current: true};
  }
  return crumbs;
}

export interface ResolvedBookmark {
  key: string;
  bookmark: Bookmark;
  label: string;
  /** The menu's groups above it, so two entries of the same name can be told apart. */
  detail: string | null;
  icon: string | null;
  path: string | null;
  queryParams: {[key: string]: string} | null;
  href: string | null;
  external: boolean;
  node: ResolvedNavNode | null;
}

/**
 * Bookmarks as the menus show them. A menu bookmark takes its label and target from the entry,
 * wherever it sits and whatever project is open - and is hidden, never dropped, when the entry
 * is not in these menus (impersonating someone, say). A page bookmark renders from its own URL.
 */
export function resolveBookmarks(bookmarks: Bookmark[], bookmarkable: ResolvedNavNode[]): ResolvedBookmark[] {
  const links = new Map(flattenLinks(bookmarkable).map((node) => [node.id, node]));
  return (bookmarks || []).reduce((resolved: ResolvedBookmark[], bookmark) => {
    if (bookmark.kind === 'menu') {
      const node = links.get(bookmark.menuId);
      if (!node) {
        return resolved;
      }
      resolved.push({
        key: `menu:${bookmark.menuId}`, bookmark, label: bookmark.label || node.label,
        detail: node.ancestors.length ? node.ancestors.join(' › ') : null,
        icon: node.icon, path: node.path, queryParams: node.queryParams, href: node.href,
        external: node.external, node,
      });
      return resolved;
    }
    const url = normalizeInAppUrl(bookmark.url);
    if (!url) {
      return resolved;
    }
    const parsed = parseInAppUrl(url);
    resolved.push({
      key: `page:${url}`, bookmark, label: bookmark.label || url, detail: null,
      icon: bookmark.icon || null, path: parsed.path,
      queryParams: Object.keys(parsed.query).length ? parsed.query : null,
      href: null, external: false, node: null,
    });
    return resolved;
  }, []);
}
